import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { createFormGroupFromObject } from '@applications/sha-services';
import { IntegrationService } from '@quorum/quorum-api';
import {
  AlternateTransportation,
  Employee,
  ServiceAppointment,
  ServiceCode,
  TechnicianGroup,
  TransportOption,
  Vehicle,
} from '@quorum/models/xs-resource';
import { GoogleAddress } from '@quorum/models/integration-resource';
import moment from 'moment';
import { Observable, Subject, of, Subscription, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, map, catchError } from 'rxjs/operators';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { Clipboard } from '@angular/cdk/clipboard';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { AlternateTransportationQueryParameters } from '@quorum/models/xs-query';

@Component({
  selector: 'svc-edit-appointment-dialog',
  templateUrl: './edit-appointment-dialog.component.html',
  styleUrls: ['./edit-appointment-dialog.component.scss'],
})
export class EditAppointmentDialogComponent implements OnInit, OnDestroy {
  formSubmitted = false;
  addTransportDetails = false;
  sameAsDropOff = false;
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

  appointmentForm: FormGroup = createFormGroupFromObject(this.dialogData.initialAppointment);
  transportationDetailsDropOffForm = createFormGroupFromObject(new AlternateTransportation());
  transportationDetailsPickUpForm = createFormGroupFromObject(new AlternateTransportation());
  destroy$ = new Subject();
  maxDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));
  numberMask = createNumberMask({
    prefix: '',
    suffix: '',
  });
  transportationDetailsDropOffExists = false;
  transportationDetailsPickUpExists = false;
  addressSuggestionsDropOff$: Observable<unknown[]>;
  addressSuggestionsPickUp$: Observable<unknown[]>;
  transportationOption$: Subscription;
  premiumTransportOptionSelected$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedDropOffAddress: string;
  selectedPickUpAddress: string;
  dropOffAddressSelected = false;
  pickUpAddressSelected = false;

  constructor(
    public dialogRef: MatDialogRef<EditAppointmentDialogComponent>,
    private clipboard: Clipboard,
    public serviceLaneFacade: ServiceLaneFacade,
    private integrationService: IntegrationService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      appointmentDropdowns: any;
      initialAppointment: ServiceAppointment;
      authenticatedEmployee: Employee;
      vehicle: Vehicle;
      isServiceCodeRequired?: boolean;
    }
  ) {}

  ngOnInit(): void {
    this.appointmentForm.addControl(
      'appointmentTime',
      new FormControl(
        this.dialogData.initialAppointment.dateTime
          ? new Date(this.dialogData.initialAppointment.dateTime).toTimeString().substring(0, 5)
          : null
      )
    );
    this.appointmentForm.addControl(
      'promisedTime',
      new FormControl(
        this.dialogData.initialAppointment.promisedDateTime
          ? new Date(this.dialogData.initialAppointment.promisedDateTime).toTimeString().substring(0, 5)
          : 'noPromisedTime'
      )
    );
    this.appointmentForm.addControl(
      'techOrTechGroup',
      new FormControl(
        this.dialogData.initialAppointment.technicianGroupId
          ? this.dialogData.initialAppointment.technicianGroupId
          : this.dialogData.initialAppointment.technicianId
      )
    );

    /**
     * If appointment already has a premium transport option on load,
     * we need to ensure the alternate details forms are properly configured
     */
    if (this.appointmentForm.get('transportOptionId').value) {
      const premiumTransportOptions = this.dialogData.appointmentDropdowns.transportOptions.filter(
        (transportOption: TransportOption) => transportOption.name.toLowerCase().includes('premium')
      );

      if (
        premiumTransportOptions
          .map((transportOption: TransportOption) => transportOption.id)
          .includes(this.appointmentForm.get('transportOptionId').value)
      ) {
        this.premiumTransportOptionSelected$.next(true);
        this.addTransportDetails = true;
        this.transportationDetailsDropOffForm
          .get('transportOptionId')
          .setValue(this.appointmentForm.get('transportOptionId').value);
        this.transportationDetailsPickUpForm
          .get('transportOptionId')
          .setValue(this.appointmentForm.get('transportOptionId').value);
        this.transportationDetailsPickUpForm.updateValueAndValidity();
        this.transportationDetailsDropOffForm.updateValueAndValidity();
      }
    }

    this.appointmentForm.get('dateTime').setValidators(Validators.required);
    this.appointmentForm.get('appointmentTime').setValidators(Validators.required);
    this.appointmentForm.get('serviceAdvisorId').setValidators(Validators.required);
    this.appointmentForm.get('odometerIn').setValidators(Validators.required);
    if (this.dialogData.isServiceCodeRequired) {
      this.appointmentForm.get('serviceCode').setValidators(Validators.required);
      // FormGroup.updateValueAndValidity() does not re-run child validators, so the
      // control must re-evaluate itself for a pre-existing blank value to register.
      this.appointmentForm.get('serviceCode').updateValueAndValidity({ emitEvent: false });
    }
    this.appointmentForm.updateValueAndValidity();

    this.transportationDetailsPickUpForm.get('transportOptionId').setValidators(Validators.required);
    this.transportationDetailsPickUpForm.get('addressLine').setValidators(Validators.required);
    this.transportationDetailsPickUpForm.get('phone').setValidators(Validators.required);
    this.transportationDetailsDropOffForm.get('transportOptionId').setValidators(Validators.required);
    this.transportationDetailsDropOffForm.get('addressLine').setValidators(Validators.required);
    this.transportationDetailsDropOffForm.get('phone').setValidators(Validators.required);
    this.transportationDetailsPickUpForm.updateValueAndValidity();
    this.transportationDetailsDropOffForm.updateValueAndValidity();

    const alternateTransportationQueryParameters = new AlternateTransportationQueryParameters({
      appointmentId: this.dialogData.initialAppointment.id.toString(),
    });
    this.serviceLaneFacade.getAlternateTransportations(alternateTransportationQueryParameters);

    this.serviceLaneFacade.alternateTransportations$
      .pipe(
        filter((alternateTransportations) => alternateTransportations != null),
        takeUntil(this.destroy$)
      )
      .subscribe((alternateTransportations: AlternateTransportation[]) => {
        if (alternateTransportations.length > 0) {
          this.addTransportDetails = true;

          alternateTransportations.forEach((alternateTransportation: AlternateTransportation) => {
            if (alternateTransportation.pickUpDropOff === 'D') {
              this.transportationDetailsDropOffForm.patchValue({
                ...alternateTransportation,
                addressLine: this.trimCountry(alternateTransportation.addressLine),
              });

              this.dropOffAddressSelected = true;
              this.selectedDropOffAddress = alternateTransportation.addressLine;
              this.transportationDetailsDropOffExists = true;
            }
            if (alternateTransportation.pickUpDropOff === 'P') {
              this.transportationDetailsPickUpForm.patchValue({
                ...alternateTransportation,
                addressLine: this.trimCountry(alternateTransportation.addressLine),
              });

              this.pickUpAddressSelected = true;
              this.selectedPickUpAddress = alternateTransportation.addressLine;
              this.transportationDetailsPickUpExists = true;
            }
          });
        } else {
          this.addTransportDetails = false;
        }
      });

    this.setupAddressAutocompleteObservables();

    this.transportationOption$ = this.appointmentForm
      .get('transportOptionId')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((selectedTransportOptionId) => {
        const selectedTransportationOption = this.dialogData.appointmentDropdowns.transportOptions.find(
          (transportOption: TransportOption) => transportOption.id === selectedTransportOptionId
        );

        if (selectedTransportationOption) {
          if (selectedTransportationOption.name.toLowerCase().includes('premium')) {
            this.addTransportDetails = true;
            this.premiumTransportOptionSelected$.next(true);
            this.transportationDetailsDropOffForm.get('transportOptionId').setValue(selectedTransportationOption.id);
            this.transportationDetailsPickUpForm.get('transportOptionId').setValue(selectedTransportationOption.id);
            this.transportationDetailsPickUpForm.updateValueAndValidity();
            this.transportationDetailsDropOffForm.updateValueAndValidity();
          } else {
            this.premiumTransportOptionSelected$.next(false);
            this.transportationDetailsDropOffForm.get('transportOptionId').setValue(null);
            this.transportationDetailsPickUpForm.get('transportOptionId').setValue(null);
            this.transportationDetailsPickUpForm.updateValueAndValidity();
            this.transportationDetailsDropOffForm.updateValueAndValidity();
          }
        }
      });

    this.transportationDetailsDropOffForm.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (value) {
          if (this.sameAsDropOff) {
            this.transportationDetailsPickUpForm.patchValue({
              transportOptionId: value.transportOptionId,
              addressLine: value.addressLine,
              phone: value.phone,
            });
          }

          if (value.addressLine !== this.trimCountry(this.selectedDropOffAddress)) {
            this.dropOffAddressSelected = false;
          }
        }
      });

    this.transportationDetailsPickUpForm.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value: any) => {
        if (value) {
          if (
            this.sameAsDropOff &&
            (value.transportOptionId !== this.transportationDetailsDropOffForm.get('transportOptionId').value ||
              value.addressLine !== this.transportationDetailsDropOffForm.get('addressLine').value ||
              value.phone !== this.transportationDetailsDropOffForm.get('phone').value)
          ) {
            this.sameAsDropOff = false;
          }

          if (value.addressLine !== this.trimCountry(this.selectedPickUpAddress)) {
            this.pickUpAddressSelected = false;
          }
        }
      });

    // disable navigate and close modal if browser's back button clicked
    history.pushState(null, '');
    window.addEventListener('popstate', () => {
      this.cancel();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cancel() {
    this.dialogRef.close();
  }

  private setupAddressAutocompleteObservables() {
    this.addressSuggestionsDropOff$ = this.transportationDetailsDropOffForm.get('addressLine').valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchQuery) => searchQuery && searchQuery.trim().length > 3),
      switchMap((searchQuery) => this.getPlacePredictions(searchQuery.trim()))
    );

    this.addressSuggestionsPickUp$ = this.transportationDetailsPickUpForm.get('addressLine').valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((searchQuery) => searchQuery && searchQuery.trim().length > 3),
      switchMap((searchQuery) => this.getPlacePredictions(searchQuery.trim()))
    );
  }

  private getPlacePredictions(addressQuery: string): Observable<GoogleAddress[]> {
    addressQuery = addressQuery.trim();
    if (addressQuery) {
      return this.integrationService.get('google-places', { params: { address: addressQuery } }).pipe(
        map((response: any) =>
          response.map(
            (place: any) =>
              new GoogleAddress({
                description: place.description,
                placeId: place.placeId,
              })
          )
        ),
        catchError((error: any) => {
          console.error('Error fetching place predictions', error);
          return of([]);
        })
      );
    } else {
      return of([]);
    }
  }

  sameAsDropOffToggle(): void {
    this.sameAsDropOff = !this.sameAsDropOff;

    if (this.sameAsDropOff) {
      const { transportOptionId, addressLine, phone, googlePlaceId } = this.transportationDetailsDropOffForm.value;
      this.selectedPickUpAddress = this.selectedDropOffAddress;
      this.pickUpAddressSelected = true;
      this.transportationDetailsPickUpForm.patchValue({
        transportOptionId,
        addressLine,
        phone,
        googlePlaceId,
      });
    }
  }

  onDropOffAddressSelected(googleAddress: GoogleAddress): void {
    this.selectedDropOffAddress = googleAddress.description;
    this.dropOffAddressSelected = true;
    if (this.sameAsDropOff) {
      this.selectedPickUpAddress = googleAddress.description;
      this.pickUpAddressSelected = true;
      this.transportationDetailsPickUpForm.patchValue({
        addressLine: this.trimCountry(googleAddress.description),
        googlePlaceId: googleAddress.placeId,
      });
    }
    this.transportationDetailsDropOffForm.patchValue({
      addressLine: this.trimCountry(googleAddress.description),
      googlePlaceId: googleAddress.placeId,
    });
  }

  onPickUpAddressSelected(googleAddress: GoogleAddress): void {
    this.selectedPickUpAddress = googleAddress.description;
    this.pickUpAddressSelected = true;
    this.transportationDetailsPickUpForm.patchValue({
      addressLine: this.trimCountry(googleAddress.description),
      googlePlaceId: googleAddress.placeId,
    });
  }

  addTransportDetailsToggle() {
    this.addTransportDetails = !this.addTransportDetails;
  }

  mapDateTimes(fieldName: string, time: string) {
  if (!time) return;

  const timeDate = new Date();
  timeDate.setHours(parseInt(time.split(':')[0]), parseInt(time.split(':')[1]));

  if (time) {
    const tempDate = this.appointmentForm.get(fieldName).value
      ? new Date(this.appointmentForm.get('dateTime').value)
      : new Date();
    tempDate.setHours(timeDate.getHours());
    tempDate.setMinutes(timeDate.getMinutes());
    this.appointmentForm.get(fieldName).setValue(tempDate);
  }
}

  techOrGroupSelected(event: any, technicians: Employee[], techGroups: TechnicianGroup[]) {
    if (technicians.findIndex((t) => t.associateId == event.value) > -1) {
      const tech: Employee = technicians.find((t) => t.associateId == event.value);
      this.appointmentForm.get('technicianId').setValue(tech.associateId);
      this.appointmentForm
        .get('technicianGroupName')
        .setValue(`${tech.embedded.associate.firstName} ${tech.embedded.associate.lastName} `);
      this.appointmentForm.get('technicianGroupId').setValue(null);
    } else {
      const group: TechnicianGroup = techGroups.find((g) => g.id == event.value);
      this.appointmentForm.get('technicianGroupId').setValue(group.id);
      this.appointmentForm.get('technicianGroupName').setValue(group.description);
      this.appointmentForm.get('technicianId').setValue(null);
    }
  }

  serviceCodeSelected(serviceCode: string, serviceCodes: ServiceCode[]) {
    const sc = serviceCodes.find((s) => s.serviceCode == serviceCode);
    let timeDate = null;

    if (sc.dateTimePromised != null && sc.activeFlag == '1') {
      timeDate = moment(sc.dateTimePromised.split('T')[1].substr(0, 5), 'hh:mm')
        .subtract(new Date().getTimezoneOffset(), 'minutes')
        .format('HH:mm');
    }

    this.appointmentForm.patchValue({ priorityNumber: sc.priorityNumber, promisedTime: timeDate });
  }

  validateNumber(event: any) {
    const key = event.key;
    const excludedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!((key >= 0 && key <= 9) || excludedKeys.includes(key))) {
      event.preventDefault();
    }
  }

  validateDateFieldInput(event: any) {
    const key = event.key;
    const excludedKeys = ['Backspace', '/', 'Delete', 'ArrowLeft', 'ArrowRight'];
    if (!((key >= 0 && key <= 9) || excludedKeys.includes(key))) {
      event.preventDefault();
    }
  }

  openExternalBookingUrl(externalAppointmentUrl: string) {
    window.open(externalAppointmentUrl, '_blank');
  }

  copyExternalBookingUrl(externalAppointmentUrl: string) {
    this.clipboard.copy(externalAppointmentUrl);

    const toastOptions: any = {
      message: `External booking URL has been copied to your clipboard.`,
      actionText: null,
      options: { duration: 3000 },
    };

    this.serviceLaneFacade.addToast(toastOptions);
  }

  save() {
    this.formSubmitted = true;

    const odometerIn: any = this.appointmentForm.get('odometerIn').value;

    if (typeof odometerIn === 'string') {
      const unmaskedOdometer = odometerIn ? parseInt(odometerIn.replace(/,/g, '')) : null;

      this.appointmentForm.patchValue({ odometerIn: unmaskedOdometer });
    }

    if (
      this.appointmentForm.get('odometerIn').value &&
      this.appointmentForm.get('odometerIn').value < this.dialogData.vehicle.currentMileage
    ) {
      this.appointmentForm.get('odometerIn').setErrors({
        invalidOdometer: this.dialogData.vehicle.currentMileage,
      });
      this.appointmentForm.updateValueAndValidity();
    }
    this.appointmentForm.markAllAsTouched();

    if (
      this.transportationDetailsDropOffForm.get('phone').value &&
      this.transportationDetailsDropOffForm.get('phone').value.replace(/\D/g, '').length != 10
    ) {
      this.transportationDetailsDropOffForm.get('phone').setErrors({ invalidPhoneNumberLength: true });
    }

    if (
      this.transportationDetailsPickUpForm.get('phone').value &&
      this.transportationDetailsPickUpForm.get('phone').value.replace(/\D/g, '').length != 10
    ) {
      this.transportationDetailsPickUpForm.get('phone').setErrors({ invalidPhoneNumberLength: true });
    }

    if (this.transportationDetailsDropOffForm.get('addressLine').value && this.dropOffAddressSelected === false) {
      this.transportationDetailsDropOffForm.get('addressLine').setErrors({ addressSelectionRequired: true });
    }

    if (this.transportationDetailsPickUpForm.get('addressLine').value && this.pickUpAddressSelected === false) {
      this.transportationDetailsPickUpForm.get('addressLine').setErrors({ addressSelectionRequired: true });
    }

    if (this.addTransportDetails === false) {
      this.resetFormErrors(this.transportationDetailsPickUpForm);
      this.resetFormErrors(this.transportationDetailsDropOffForm);
    }

    this.transportationDetailsPickUpForm.markAllAsTouched();
    this.transportationDetailsDropOffForm.markAllAsTouched();

    if (
      this.appointmentForm.valid &&
      this.transportationDetailsDropOffForm.valid &&
      this.transportationDetailsPickUpForm.valid
    ) {
      const updatedAppointment = this.appointmentForm.getRawValue();
      const transportationDetailsDropOff = this.transportationDetailsDropOffForm.getRawValue();
      const transportationDetailsPickUp = this.transportationDetailsPickUpForm.getRawValue();

      this.dialogRef.close({
        updatedAppointment: updatedAppointment,
        addTransportDetails: this.addTransportDetails,
        transportationDetailsDropOff: {
          ...transportationDetailsDropOff,
          addressLine: this.selectedDropOffAddress,
          appointmentId: updatedAppointment.id,
          phone: transportationDetailsDropOff?.phone?.replace(/\D+/g, ''),
          pickUpDropOff: 'D',
        },
        transportationDetailsPickUp: {
          ...transportationDetailsPickUp,
          addressLine: this.selectedPickUpAddress,
          appointmentId: updatedAppointment.id,
          phone: transportationDetailsPickUp?.phone?.replace(/\D+/g, ''),
          pickUpDropOff: 'P',
        },
        transportationDetailsDropOffExists: this.transportationDetailsDropOffExists,
        transportationDetailsPickUpExists: this.transportationDetailsPickUpExists,
      });
    }
  }

  resetFormErrors(form: FormGroup) {
    Object.keys(form.controls).forEach((field) => {
      form.get(field).setErrors(null);
    });
  }

  clearAddressInput(pickUpDropOff: string) {
    if (pickUpDropOff === 'DropOff') {
      this.transportationDetailsDropOffForm.get('addressLine').setValue('');
    }

    if (pickUpDropOff === 'PickUp') {
      this.transportationDetailsPickUpForm.get('addressLine').setValue('');
    }
  }

  trimCountry(address: string) {
    return address?.replace(/,[^,]+$/, '');
  }
}
